import { selectDraftBoardForm } from "app/containers/Features/containers/Boards/components/Settings/redux/selector";
import { selectTicketGroups } from "app/containers/Features/containers/Boards/containers/Tickets/redux/selector";
import { ticketGroupsActions } from "app/containers/Features/containers/Boards/containers/Tickets/redux/slice";
import { ITicketFormData } from "app/containers/Features/containers/Boards/containers/Tickets/types";
import { getGroupDefaultTicketFields } from "app/containers/Features/containers/Boards/helpers";
import { GroupByTypeEnum } from "app/containers/Features/containers/Boards/types";
import {
  FieldCategoryTypeEnum,
  ITicketTypes,
} from "app/containers/Features/containers/TeamSettings/container/Projects/container/WorkItems/types";
import {
  DESIGNED_FIELDS,
  getVisibleFields,
  splitColumns,
} from "app/containers/Features/containers/Ticket/components/TicketDetailsComponent/TicketContextFields/contextFieldConfig";
import { selectTicketStatus } from "app/redux/selector";
import { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

interface Props {
  form: ITicketFormData;
  ticketTypes: ITicketTypes[];
  projectId: string;
  createTicketForHome: boolean;
}

export const useContextFields = ({
  form,
  ticketTypes,
  projectId,
  createTicketForHome,
}: Props) => {
  const dispatch = useDispatch();
  const statusData = useSelector(selectTicketStatus);
  const groups = useSelector(selectTicketGroups);
  const boardFormData = useSelector(selectDraftBoardForm);

  const setValue = useCallback(
    (name: string, value: any) => {
      if (name === "tags") {
        dispatch(
          ticketGroupsActions.setTicketTagsFormValue({
            ids: value,
          }),
        );
      } else {
        dispatch(ticketGroupsActions.setTicketFormValue({ name, value }));
      }
    },
    [dispatch],
  );

  useEffect(() => {
    if (!statusData?.length) return;

    let defaultStatusId = statusData[0]._id;

    if (boardFormData.groupBy === GroupByTypeEnum.STATUS) {
      const group = groups.find((g) => g._id === projectId);
      if (group) {
        const resolved = getGroupDefaultTicketFields(
          group,
          GroupByTypeEnum.STATUS,
        );
        const matched = statusData.find((s) => s.name === resolved.status);
        if (matched) defaultStatusId = matched._id;
      }
    }

    setValue("status", defaultStatusId);
  }, [statusData, boardFormData.groupBy, groups, projectId, setValue]);

  const contextFields = useMemo(
    () =>
      getVisibleFields(
        ticketTypes,
        form.type,
        FieldCategoryTypeEnum.CONTEXT_FIELDS,
      ).filter((f) => DESIGNED_FIELDS.includes(f.fieldToolTip)),
    [ticketTypes, form.type],
  );

  const headerFields = useMemo(
    () =>
      getVisibleFields(
        ticketTypes,
        form.type,
        FieldCategoryTypeEnum.HEADER_FIELDS,
      ).filter((f) => DESIGNED_FIELDS.includes(f.fieldToolTip)),
    [ticketTypes, form.type],
  );

  const visibleFields = useMemo(
    () =>
      createTicketForHome ? [...headerFields, ...contextFields] : contextFields,
    [createTicketForHome, headerFields, contextFields],
  );

  const { left, right } = splitColumns(
    visibleFields,
    createTicketForHome ? 4 : 3,
  );

  return { left, right, setValue };
};
